import { useState } from 'react';
import { AxiosError } from 'axios';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  IconButton,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  useRecycleBin,
  useRestoreFile,
  useRestoreFolder,
  usePermanentDeleteFile,
  usePermanentDeleteFolder,
} from '../../hooks/useRecycleBin';
import { RestoreCollisionDialog } from './RestoreCollisionDialog';
import type { RecycleBinItemDto } from '../../api/recycleBinApi';
import { formatFileSize } from './formatFileSize';

interface RecycleBinListProps {
  siteId?: number;
}

interface TreeNode {
  item: RecycleBinItemDto;
  children: TreeNode[];
}

function buildTree(items: RecycleBinItemDto[]): TreeNode[] {
  const itemMap = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  for (const item of items) {
    itemMap.set(item.id, { item, children: [] });
  }

  for (const item of items) {
    const node = itemMap.get(item.id)!;
    const parentNode = item.originalParentId
      ? itemMap.get(item.originalParentId)
      : undefined;

    if (parentNode) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children: folders first, then by name
  const sortChildren = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.item.type !== b.item.type) {
        return a.item.type === 'folder' ? -1 : 1;
      }
      return a.item.name.localeCompare(b.item.name);
    });
    for (const node of nodes) {
      sortChildren(node.children);
    }
  };
  sortChildren(roots);

  return roots;
}

function getCountdownDays(scheduledPurgeAt: string | null): number {
  if (!scheduledPurgeAt) {
    return 0;
  }
  const purgeDate = new Date(scheduledPurgeAt);
  const now = new Date();
  const diffMs = purgeDate.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

function getCountdownColor(days: number): 'default' | 'warning' | 'error' {
  if (days <= 1) {
    return 'error';
  }
  if (days <= 3) {
    return 'warning';
  }
  return 'default';
}

function getCountdownLabel(days: number): string {
  if (days === 0) {
    return 'Expires today';
  }
  if (days === 1) {
    return '1 day left';
  }
  return `${days} days left`;
}

function RecycleBinTreeNode({
  node,
  depth,
  expanded,
  onToggle,
  onRestore,
  onDelete,
}: {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onRestore: (item: RecycleBinItemDto) => void;
  onDelete: (item: RecycleBinItemDto) => void;
}) {
  const { item } = node;
  const isFolder = item.type === 'folder';
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(item.id);
  const days = getCountdownDays(item.scheduledPurgeAt);
  const chipColor = getCountdownColor(days);
  const chipLabel = getCountdownLabel(days);
  const isTopLevel = depth === 0;

  return (
    <>
      <ListItemButton
        disableRipple={!isFolder || !hasChildren}
        onClick={() => {
          if (isFolder && hasChildren) {
            onToggle(item.id);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          pl: { xs: 2 + depth * 3, sm: 3 + depth * 3 },
          pr: { xs: 2, sm: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: isFolder && hasChildren ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor:
              isFolder && hasChildren ? undefined : 'transparent',
          },
        }}
        data-testid={`recycle-row-${item.id}`}
      >
        {isFolder && hasChildren ? (
          isExpanded ? (
            <ExpandMoreIcon sx={{ fontSize: 18, color: 'action.active' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: 'action.active' }} />
          )
        ) : (
          <Box sx={{ width: 18 }} />
        )}

        {isFolder ? (
          <FolderOutlinedIcon sx={{ fontSize: 20, color: 'action.active' }} />
        ) : (
          <InsertDriveFileOutlinedIcon
            sx={{ fontSize: 20, color: 'action.active' }}
          />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {item.name}
          </Typography>
          {isTopLevel && item.originalFolderPath && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
            >
              {item.originalFolderPath}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            {!isFolder && item.fileSizeBytes != null
              ? formatFileSize(item.fileSizeBytes)
              : ''}
            {isTopLevel && item.originalSiteName
              ? ` · ${item.originalSiteName}`
              : ''}
            {isTopLevel && item.deletedBy
              ? ` · Deleted by ${item.deletedBy}`
              : ''}
          </Typography>
        </Box>

        {isTopLevel && (
          <Chip
            label={chipLabel}
            size="small"
            color={chipColor}
            variant="outlined"
          />
        )}

        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            onRestore(item);
          }}
          title="Restore"
          data-testid={`restore-button-${item.id}`}
        >
          <RestoreIcon fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            onDelete(item);
          }}
          title="Delete forever"
          data-testid={`permanent-delete-button-${item.id}`}
        >
          <DeleteForeverIcon fontSize="small" />
        </IconButton>
      </ListItemButton>

      {isFolder && hasChildren && (
        <Collapse in={isExpanded} unmountOnExit>
          {node.children.map(child => (
            <RecycleBinTreeNode
              key={child.item.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onRestore={onRestore}
              onDelete={onDelete}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

export function RecycleBinList({ siteId }: RecycleBinListProps) {
  const { data: items, isLoading } = useRecycleBin(siteId);
  const restoreFile = useRestoreFile(siteId);
  const restoreFolder = useRestoreFolder(siteId);
  const permanentDeleteFile = usePermanentDeleteFile(siteId);
  const permanentDeleteFolder = usePermanentDeleteFolder(siteId);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<RecycleBinItemDto | null>(
    null
  );
  const [collisionInfo, setCollisionInfo] = useState<{
    fileId: string;
    fileName: string;
    conflictInfo: {
      conflictingFileId: string;
      conflictingFileName: string;
      targetFolderId: string | null;
    };
  } | null>(null);

  const handleToggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRestore = async (item: RecycleBinItemDto) => {
    if (item.type === 'folder') {
      restoreFolder.mutate(item.id);
      return;
    }

    try {
      await restoreFile.mutateAsync({ fileId: item.id });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        const data = error.response.data as
          | {
              conflictingFileId?: string;
              conflictingFileName?: string;
              targetFolderId?: string | null;
            }
          | undefined;

        if (data?.conflictingFileId && data.conflictingFileName) {
          setCollisionInfo({
            fileId: item.id,
            fileName: item.name,
            conflictInfo: {
              conflictingFileId: data.conflictingFileId,
              conflictingFileName: data.conflictingFileName,
              targetFolderId: data.targetFolderId ?? null,
            },
          });
        }
      }
    }
  };

  const handlePermanentDelete = (item: RecycleBinItemDto) => {
    if (item.type === 'folder') {
      permanentDeleteFolder.mutate(item.id);
    } else {
      permanentDeleteFile.mutate(item.id);
    }
    setDeleteConfirm(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const tree = items ? buildTree(items) : [];

  return (
    <Box data-testid="recycle-bin-list">
      <Alert severity="info" sx={{ mx: { xs: 2, sm: 3 }, mt: 2, mb: 1 }}>
        Files in the recycle bin are permanently deleted after 7 days.
      </Alert>

      {tree.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 6,
            gap: 1,
          }}
        >
          <DeleteForeverIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary">
            Recycle bin is empty
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {tree.map(node => (
            <RecycleBinTreeNode
              key={node.item.id}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={handleToggle}
              onRestore={handleRestore}
              onDelete={setDeleteConfirm}
            />
          ))}
        </List>
      )}

      {/* Permanent delete confirmation */}
      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
      >
        <DialogTitle>Permanently delete?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete &quot;{deleteConfirm?.name}&quot;? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirm(null)}
            data-testid="permanent-delete-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              deleteConfirm && handlePermanentDelete(deleteConfirm)
            }
            color="error"
            variant="contained"
            data-testid="permanent-delete-confirm-button"
          >
            Delete forever
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore collision dialog */}
      {collisionInfo && (
        <RestoreCollisionDialog
          open
          onClose={() => setCollisionInfo(null)}
          conflictInfo={collisionInfo.conflictInfo}
          fileId={collisionInfo.fileId}
          fileName={collisionInfo.fileName}
          siteId={siteId}
        />
      )}
    </Box>
  );
}
